import { EducationJourneyProgramStatusResponse } from "@9amhealth/openapi";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type { UserJourneyModule } from "components/UserJourneyOverview/UserJourneyBloc";

const STORAGE_KEY = "user-journey-hydration";

interface HydrationPayload {
  userId: string;
  journey: string;
  taskProgram: string;
  modules: UserJourneyModule[];
  journeys: EducationJourneyProgramStatusResponse[];
}

interface ReadArgs {
  journey: string;
  taskProgram: string;
}

interface WriteArgs extends ReadArgs {
  modules: UserJourneyModule[];
  journeys: EducationJourneyProgramStatusResponse[];
}

// Synchronous localStorage mirror of UserJourneyBloc's derived `{ modules, journeys }`,
// tagged with the active user id so a post-switch read returns null instead of leaking
// the previous user's journey. Enables seeding the bloc's initial state before the
// async IndexedDB chain resolves, so the /journey spinner doesn't flash on revisit.
export const UserJourneyHydrationCache = {
  read({
    journey,
    taskProgram
  }: ReadArgs): Pick<HydrationPayload, "modules" | "journeys"> | null {
    const { activeUserId } = StorageController;
    if (!activeUserId) {
      return null;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as HydrationPayload;
      if (
        parsed.userId !== activeUserId ||
        parsed.journey !== journey ||
        parsed.taskProgram !== taskProgram
      ) {
        return null;
      }
      return { modules: parsed.modules, journeys: parsed.journeys };
    } catch {
      return null;
    }
  },

  write({ journey, taskProgram, modules, journeys }: WriteArgs): void {
    const { activeUserId } = StorageController;
    if (!activeUserId) {
      return;
    }
    try {
      const payload: HydrationPayload = {
        userId: activeUserId,
        journey,
        taskProgram,
        modules,
        journeys
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage full or unavailable — hydration is best-effort.
    }
  }
};

export const clearUserJourneyHydration = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
